import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { color, radius } from '../tokens';
import { haptic } from '../../telegram';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxHeight?: number | string;
}

const wrapStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1400,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};
const backdropHidden = { opacity: 0 };
const backdropShown = { opacity: 1 };
const backdropStyle: React.CSSProperties = { position: 'absolute', inset: 0, background: color.overlay };
const panelHidden = { y: '100%' };
const panelShown = { y: 0 };
const panelTransition = { type: 'spring' as const, stiffness: 360, damping: 36 };
const dragConstraints = { top: 0, bottom: 0 };
const grabberWrap: React.CSSProperties = { display: 'flex', justifyContent: 'center', padding: '10px 0 4px' };
const grabber: React.CSSProperties = { width: 40, height: 4, borderRadius: 999, background: color.border };
const titleRow: React.CSSProperties = { padding: '4px 20px 8px' };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: 18, fontWeight: 700, color: color.text };
const bodyStyle: React.CSSProperties = { padding: '4px 20px 20px', overflowY: 'auto' };

/**
 * Bottom sheet built on the new design tokens. Preferred over modals on mobile.
 * Swipe-down or backdrop tap closes it; body scroll is locked while open.
 */
export default function Sheet({ open, onClose, title, children, maxHeight = '85vh' }: SheetProps) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const panelStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    maxWidth: 640,
    margin: '0 auto',
    background: color.surface,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    border: `1px solid ${color.border}`,
    maxHeight,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  };
  const closeBackdrop = () => { onClose(); haptic('light'); };
  const handleDragEnd = (_e: unknown, info: { offset: { y: number } }) => {
    if (info.offset.y > 120) closeBackdrop();
  };

  return (
    <AnimatePresence>
      {open ? (
        <div style={wrapStyle}>
          <motion.div
            initial={backdropHidden}
            animate={backdropShown}
            exit={backdropHidden}
            onClick={closeBackdrop}
            style={backdropStyle}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={panelHidden}
            animate={panelShown}
            exit={panelHidden}
            transition={panelTransition}
            drag="y"
            dragConstraints={dragConstraints}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            style={panelStyle}
          >
            <div style={grabberWrap}>
              <div style={grabber} />
            </div>
            {title !== undefined ? (
              <div style={titleRow}>
                <h2 style={titleStyle}>{title}</h2>
              </div>
            ) : null}
            <div style={bodyStyle}>{children}</div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
